'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { TbArrowLeft as ArrowLeft, TbArrowRight as ArrowRight } from 'react-icons/tb';
import HoverTiltCard from '../animations/HoverTiltCard';
import ServiceStickerBadge from './ServiceStickerBadge';
import { getMediaUrl } from '@/app/lib/media';

interface ProjectCardProps {
    project: any;
    locale: string;
    isAr?: boolean;
    t: {
        viewProject: string;
    };
}

export default function ProjectCard({
    project,
    locale,
    isAr = false,
    t,
}: ProjectCardProps) {
    const [isHovered, setIsHovered] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const imgUrl = getMediaUrl(project.heroImage, 'card');
    const videoUrl = project.featuredVideoUrl || getMediaUrl(project.featuredVideo);

    useEffect(() => {
        if (!videoRef.current || !videoUrl) return;

        if (isHovered) {
            videoRef.current.play().catch(() => {
                // Ignore autoplay/play blocks (silent catch)
            });
        } else {
            videoRef.current.pause();
            try {
                videoRef.current.currentTime = 0;
            } catch (e) {
                // ignore
            }
        }
    }, [isHovered, videoUrl]);

    const services = Array.isArray(project.services) ? project.services : [];
    const relatedService = services.find((service: any) => service && typeof service === 'object') || null;

    const sticker = {
        slug: relatedService?.slug,
        kind: project.stickerKind || relatedService?.stickerKind,
        variant: project.stickerVariant || relatedService?.stickerVariant,
    };

    return (
        <Link 
            href={`/${locale}/work/${project.slug}`} 
            className="block h-full w-full"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <HoverTiltCard intensity={15} className="h-full w-full">
                <div className="group relative flex h-full min-h-[430px] flex-col justify-end overflow-hidden rounded-[1.5rem] border border-brand-text/10 bg-brand-bg/5 p-10 shadow-[0_24px_70px_rgba(0,0,0,0.16)]">
                    
                    {/* Media Container */}
                    <div className="absolute inset-0 z-0 bg-black/10 transition-colors duration-500 group-hover:bg-black/20">
                        {/* Video Layer if available - hover plays as cover */}
                        {videoUrl && (
                            <video
                                aria-hidden="true"
                                ref={videoRef}
                                src={videoUrl}
                                loop
                                muted
                                playsInline
                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 z-10 ${
                                    isHovered ? 'opacity-90' : 'opacity-0 pointer-events-none'
                                }`}
                            />
                        )}

                        {/* Image Layer - static contained */}
                        {imgUrl && (
                            <Image 
                                src={imgUrl} 
                                alt={project.title}
                                fill
                                sizes="(max-width: 768px) 100vw, 50vw"
                                className="object-contain p-4 opacity-70 group-hover:opacity-90 transition-all duration-700 group-hover:scale-105"
                            />
                        )}
                    </div>
                    
                    {/* Soft dark gradient mask for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/95 via-brand-bg/50 to-transparent z-10" />

                    <ServiceStickerBadge
                        slug={sticker.slug}
                        kind={sticker.kind}
                        variant={sticker.variant}
                        align={isAr ? 'start' : 'end'}
                        sizeClassName="w-28 sm:w-36 md:w-40"
                    />

                    {/* Content Layer */}
                    <div className="relative z-20 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        {project.client && (
                            <span className="mb-4 inline-block rounded-full bg-brand-cta/20 px-3 py-1 text-sm font-bold uppercase tracking-widest text-brand-cta">
                                {project.client}
                            </span>
                        )}
                        
                        <h3 className="text-4xl font-heading font-bold mb-4 text-brand-text">
                            {project.title}
                        </h3>
                        
                        <p className="text-lg text-brand-text/80 font-body line-clamp-2 mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                            {project.shortDescription}
                        </p>
                        
                        <div className="inline-flex items-center text-brand-cta font-bold uppercase tracking-widest text-sm">
                            {t.viewProject}
                            {isAr ? <ArrowLeft className="mr-2 w-4 h-4" /> : <ArrowRight className="ml-2 w-4 h-4" />}
                        </div>
                    </div>
                </div>
            </HoverTiltCard>
        </Link>
    );
}
